import {
  BaseAlert,
  BaseBadge,
  BaseButton,
  BaseCard,
  BaseModal,
  BasePagination,
  BaseSelect,
  BaseTable,
} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {
  useActivateBlacklistEntry,
  useBlacklist,
  useCreateBlacklistEntry,
  useDeactivateBlacklistEntry,
} from "@/hooks/useBlacklist";
import type {
  BlacklistEntry,
  BlacklistEntryType,
  BlacklistSource,
  CreateBlacklistBody,
} from "@/services/blacklist.service";
import {Ban, Search, ShieldCheck, ShieldOff} from "lucide-react";
import {useState} from "react";

/* ── Label maps ────────────────────────────────────────────── */

const entryTypeLabel: Record<BlacklistEntryType, string> = {
  id_document: "Documento ID",
  full_name: "Nombre completo",
  phone: "Teléfono",
  email: "Email",
  bank_account: "Cuenta bancaria",
  iban_clabe_cbu: "IBAN / CLABE / CBU",
  card_partial: "Tarjeta (parcial)",
  crypto_address: "Dirección crypto",
  ip: "Dirección IP",
  device_fingerprint: "Device fingerprint",
  sanctioned_country: "País sancionado",
};

const sourceLabel: Record<BlacklistSource, string> = {
  manual: "Manual",
  ofac: "OFAC",
  un: "ONU",
  local_list: "Lista local",
};

const entryTypeOptions = Object.entries(entryTypeLabel).map(([value, label]) => ({value, label}));
const sourceOptions = Object.entries(sourceLabel).map(([value, label]) => ({value, label}));

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-CL", {dateStyle: "medium", timeStyle: "short"}).format(new Date(iso));
};

/* ── Main Page ─────────────────────────────────────────────── */

export function Blacklist() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [showCreate, setShowCreate] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [entryTypeFilter, setEntryTypeFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("");

  const filters = {
    page,
    limit,
    ...(searchQuery ? {search: searchQuery} : {}),
    ...(entryTypeFilter ? {entryType: entryTypeFilter} : {}),
    ...(sourceFilter ? {source: sourceFilter} : {}),
    ...(activeFilter ? {isActive: activeFilter === "active"} : {}),
  };

  const {data, isLoading} = useBlacklist(filters);
  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const activateMutation = useActivateBlacklistEntry();
  const deactivateMutation = useDeactivateBlacklistEntry();
  const [toggleTarget, setToggleTarget] = useState<BlacklistEntry | null>(null);

  const handleToggle = (entry: BlacklistEntry) => {
    setToggleTarget(entry);
  };

  const confirmToggle = () => {
    if (!toggleTarget) return;
    if (toggleTarget.isActive) {
      deactivateMutation.mutate(toggleTarget.id);
    } else {
      activateMutation.mutate(toggleTarget.id);
    }
    setToggleTarget(null);
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setEntryTypeFilter("");
    setSourceFilter("");
    setActiveFilter("");
    setPage(1);
  };

  const hasActiveFilters = searchQuery || entryTypeFilter || sourceFilter || activeFilter;

  const columns: Column<BlacklistEntry>[] = [
    {
      key: "entryType",
      header: "Tipo",
      render: row => (
        <span className="text-sm font-medium text-(--color-text-primary)">
          {entryTypeLabel[row.entryType] ?? row.entryType}
        </span>
      ),
    },
    {
      key: "value",
      header: "Valor",
      render: row => <span className="text-sm text-(--color-text-secondary) font-mono">{row.value}</span>,
    },
    {key: "reason", header: "Motivo"},
    {
      key: "source",
      header: "Fuente",
      render: row => (
        <BaseBadge
          variant={row.source === "manual" ? "info" : row.source === "ofac" ? "danger" : "warning"}
          label={sourceLabel[row.source] ?? row.source}
        />
      ),
    },
    {
      key: "isActive",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={row.isActive ? "danger" : "neutral"}
          label={row.isActive ? "Activo" : "Inactivo"}
          dot
        />
      ),
    },
    {
      key: "createdAt",
      header: "Creado",
      render: row => <span className="text-xs text-(--color-text-tertiary)">{formatDate(row.createdAt)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: row => (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            handleToggle(row);
          }}
          className={[
            "p-1.5 rounded-md transition-colors",
            row.isActive
              ? "text-(--color-text-secondary) hover:text-(--color-success) hover:bg-emerald-50"
              : "text-(--color-text-secondary) hover:text-(--color-danger) hover:bg-red-50",
          ].join(" ")}
          title={row.isActive ? "Desactivar" : "Activar"}
        >
          {row.isActive ? <ShieldOff size={16} /> : <ShieldCheck size={16} />}
        </button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">Blacklist</h1>
          <p className="text-sm text-(--color-text-secondary) mt-1">{pagination.total} entradas registradas</p>
        </div>
        <BaseButton
          variant="primary"
          size="md"
          onClick={() => setShowCreate(true)}
        >
          <Ban size={16} /> Nueva entrada
        </BaseButton>
      </div>

      {/* Filters */}
      <BaseCard padding="md">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-50">
            <label
              htmlFor="bl-search"
              className="text-sm font-medium text-(--color-text) mb-1.5 block"
            >
              Buscar
            </label>
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-(--color-text-tertiary)"
              />
              <input
                id="bl-search"
                type="text"
                placeholder="Valor, motivo..."
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-white border border-(--color-border) rounded-md py-2 pl-9 pr-3 text-sm text-(--color-text) outline-none focus:border-(--color-accent) focus:ring-2 focus:ring-(--color-accent)/20"
              />
            </div>
          </div>
          <div className="w-48">
            <BaseSelect
              label="Tipo"
              placeholder="Todos los tipos"
              options={entryTypeOptions}
              value={entryTypeFilter}
              onChange={e => {
                setEntryTypeFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-36">
            <BaseSelect
              label="Fuente"
              placeholder="Todas"
              options={sourceOptions}
              value={sourceFilter}
              onChange={e => {
                setSourceFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-36">
            <BaseSelect
              label="Estado"
              placeholder="Todos"
              options={[
                {value: "active", label: "Activo"},
                {value: "inactive", label: "Inactivo"},
              ]}
              value={activeFilter}
              onChange={e => {
                setActiveFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          {hasActiveFilters && (
            <BaseButton
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
            >
              Limpiar filtros
            </BaseButton>
          )}
        </div>
      </BaseCard>

      {/* Table */}
      <BaseCard padding="none">
        <BaseTable<BlacklistEntry>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No hay entradas en la blacklist"
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>

      {/* Create Modal */}
      <CreateBlacklistModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
      />

      <BaseAlert
        open={toggleTarget !== null}
        onClose={() => setToggleTarget(null)}
        onConfirm={confirmToggle}
        variant="warning"
        title={toggleTarget?.isActive ? "Desactivar entrada" : "Activar entrada"}
        description={
          toggleTarget
            ? `¿Desea ${toggleTarget.isActive ? "desactivar" : "activar"} la entrada "${toggleTarget.value}"?`
            : ""
        }
      />
    </div>
  );
}

/* ── Create Modal ──────────────────────────────────────────── */

function CreateBlacklistModal({open, onClose}: {open: boolean; onClose: () => void}) {
  const createMutation = useCreateBlacklistEntry();
  const [form, setForm] = useState<CreateBlacklistBody>({
    entryType: "email",
    value: "",
    reason: "",
    source: "manual",
  });

  const handleSubmit = () => {
    if (!form.value.trim() || !form.reason.trim()) return;
    createMutation.mutate(form, {
      onSuccess: () => {
        setForm({entryType: "email", value: "", reason: "", source: "manual"});
        onClose();
      },
    });
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Nueva Entrada Blacklist"
      size="md"
      footer={
        <>
          <BaseButton
            variant="secondary"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!form.value.trim() || !form.reason.trim()}
            loading={createMutation.isPending}
          >
            Crear entrada
          </BaseButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <BaseSelect
          label="Tipo de entrada"
          options={entryTypeOptions}
          value={form.entryType}
          onChange={e => setForm(p => ({...p, entryType: e.target.value as BlacklistEntryType}))}
        />

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="bl-value"
            className="text-sm font-medium text-(--color-text)"
          >
            Valor
          </label>
          <input
            id="bl-value"
            placeholder="ej. usuario@email.com, +56912345678, 192.168.1.1"
            value={form.value}
            onChange={e => setForm(p => ({...p, value: e.target.value}))}
            className="w-full bg-white border border-(--color-border) rounded-md py-2 px-3 text-sm text-(--color-text) outline-none focus:border-(--color-accent) focus:ring-2 focus:ring-(--color-accent)/20"
          />
        </div>

        <BaseSelect
          label="Fuente"
          options={sourceOptions}
          value={form.source}
          onChange={e => setForm(p => ({...p, source: e.target.value as BlacklistSource}))}
        />

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="bl-reason"
            className="text-sm font-medium text-(--color-text)"
          >
            Motivo
          </label>
          <textarea
            id="bl-reason"
            placeholder="Razón por la que se agrega a la blacklist..."
            value={form.reason}
            onChange={e => setForm(p => ({...p, reason: e.target.value}))}
            rows={3}
            className="w-full bg-white border border-(--color-border) rounded-md py-2 px-3 text-sm text-(--color-text) outline-none focus:border-(--color-accent) focus:ring-2 focus:ring-(--color-accent)/20 resize-none"
          />
        </div>
      </div>
    </BaseModal>
  );
}
